import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';
import { fuzzy } from './todoNav';

/**
 * THE COMMAND PALETTE (Ctrl/Cmd K) - Linear's and Todoist's, scoped to the
 * to-do page. The safety net for everything that moved under More: every
 * view, list, label, saved filter, open to-do and note, and the actions.
 *
 *   An empty box: Recent (the last five places), Go to (the fixed entries
 *   and every view in More), Favourites, My lists, Actions.
 *   Typing: a fuzzy match over all of it, with "Add to-do “…”" first, which
 *   sends the words through quick add - "ring Rita fri 3pm p1" works from
 *   anywhere.
 *
 * Keyboard: ↑ ↓ move, Enter runs, Escape closes; the focus goes back where
 * it came from. A combobox over a listbox, so a screen reader hears the
 * option it is on. Full screen on a phone.
 *
 * `groups` comes from TodoApp: [{ key, label, items: [{ id, label, sub, icon,
 * mark, hint, run, keywords }] }], `todos` the open to-dos and notes
 * ([{ id, title, where, note, run }]), and `onAdd(text)` adds one.
 */
export default function TodoPalette({ groups, todos, onAdd, onClose, placeholder = 'Search or type a command…' }) {
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const input = useRef(null);
  const list = useRef(null);
  const back = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  useEffect(() => {
    if (input.current) input.current.focus();
    const body = document.body;
    const was = body.style.overflow;
    body.style.overflow = 'hidden';
    const from = back.current;
    return () => {
      body.style.overflow = was;
      if (from && typeof from.focus === 'function' && document.contains(from)) {
        try { from.focus({ preventScroll: true }); } catch { /* gone */ }
      }
    };
  }, []);

  const term = q.trim();
  const shown = useMemo(() => {
    if (!term) {
      return groups.filter((g) => !g.searchOnly).map((g) => ({ ...g, items: g.items.slice(0, g.limit || 50) }))
        .filter((g) => g.items.length);
    }
    const score = (it) => Math.max(fuzzy(it.label, term), fuzzy(`${it.label} ${it.keywords || ''}`, term) * 0.8);
    const out = [];
    out.push({ key: 'add', label: null, items: [{ id: 'add-typed', label: `Add to-do “${term}”`, icon: 'plus', hint: 'Enter',
                                                   run: () => onAdd(term) }] });
    for (const g of groups) {
      if (g.recent) continue;
      const hits = g.items.map((it) => [it, score(it)]).filter(([, s]) => s > 0)
        .sort((a, b) => b[1] - a[1]).slice(0, g.searchLimit || 8).map(([it]) => it);
      if (hits.length) out.push({ ...g, items: hits });
    }
    const found = todos.map((t) => [t, fuzzy(t.title, term)]).filter(([, s]) => s > 0)
      .sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([t]) => ({ id: `todo-${t.id}`, label: t.title || 'Untitled note', sub: t.where, icon: t.note ? 'note' : 'done',
                      run: t.run }));
    if (found.length) {
      // To-dos read best right after the places, before the actions.
      const actions = out.findIndex((g) => g.key === 'actions');
      out.splice(actions < 0 ? out.length : actions, 0, { key: 'todos', label: 'To-dos and notes', items: found });
    }
    return out;
  }, [groups, todos, term, onAdd]);
  const flat = useMemo(() => shown.flatMap((g) => g.items), [shown]);

  useEffect(() => { setAt(0); }, [term]);
  useEffect(() => {
    const el = list.current && list.current.querySelector(`[data-pal-index="${at}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [at]);

  const run = (it) => {
    if (!it) return;
    onClose();
    // After the palette has gone and the focus is back, so what it opens can take it.
    setTimeout(() => it.run(), 0);
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown' || (e.key === 'n' && e.ctrlKey)) { e.preventDefault(); setAt((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp' || (e.key === 'p' && e.ctrlKey)) { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setAt(0); }
    else if (e.key === 'End') { e.preventDefault(); setAt(Math.max(0, flat.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(flat[at]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (e.key === 'Tab') e.preventDefault();
  };

  let index = -1;
  return (
    <div className="tdk-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tdk" role="dialog" aria-modal="true" aria-label="Search and commands">
        <div className="tdk-search">
          <Icon name="search" size={18} className="tdk-search-ic" />
          <input ref={input} type="text" className="tdk-input" value={q} placeholder={placeholder}
                 role="combobox" aria-expanded="true" aria-controls="tdk-list" aria-autocomplete="list"
                 aria-activedescendant={flat[at] ? `tdk-opt-${at}` : undefined} aria-label="Search to-dos, views and commands"
                 onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} autoComplete="off" spellCheck={false} />
          <button type="button" className="tdk-close" aria-label="Close" onClick={onClose}><Icon name="close" size={16} /></button>
        </div>
        <div className="tdk-list" id="tdk-list" role="listbox" aria-label="Results" ref={list}>
          {flat.length === 0 && <p className="tdk-none">Nothing matches “{term}”.</p>}
          {shown.map((g) => (
            <div key={g.key} role="group" aria-label={g.label || 'Add'} className="tdk-group" data-pal-group={g.key}>
              {g.label && <p className="tdk-group-h" aria-hidden="true">{g.label}</p>}
              {g.items.map((it) => {
                index += 1;
                const i = index;
                return (
                  <div key={`${g.key}-${it.id}`} id={`tdk-opt-${i}`} role="option" aria-selected={i === at}
                       data-pal-index={i} data-pal-item={it.id}
                       className={`tdk-opt${i === at ? ' is-on' : ''}`}
                       onMouseMove={() => { if (at !== i) setAt(i); }}
                       onMouseDown={(e) => e.preventDefault()}
                       onClick={() => run(it)}>
                    <span className="tdk-ic" aria-hidden="true">{it.mark || <Icon name={it.icon || 'chevronRight'} size={15} />}</span>
                    <span className="tdk-label">{it.label}</span>
                    {it.sub && <span className="tdk-sub">{it.sub}</span>}
                    {it.hint && <kbd className="tdk-hint">{it.hint}</kbd>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <p className="tdk-foot" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> to move</span><span><kbd>Enter</kbd> to open</span><span><kbd>Esc</kbd> to close</span>
        </p>
      </div>
    </div>
  );
}

/** THE "?" SHEET: every key the page answers to, in groups. */
export const SHORTCUTS = [
  { title: 'Anywhere on the page', keys: [
    [['Ctrl', 'K'], 'Search and commands'], [['Q'], 'Add a to-do'], [['/'], 'Search this view'], [['?'], 'This list of keys'],
    [['Esc'], 'Close a panel, sheet or menu']] },
  { title: 'Go to', keys: [
    [['G', 'I'], 'Inbox'], [['G', 'T'], 'Today'], [['G', 'U'], 'Upcoming'], [['G', 'M'], 'Assigned to me'],
    [['G', 'N'], 'Notes'], [['G', 'A'], 'All to-dos'], [['G', 'C'], 'Upcoming as a calendar']] },
  { title: 'Layout', keys: [[['V', 'L'], 'List'], [['V', 'B'], 'Board (a list)'], [['V', 'C'], 'Calendar']] },
  { title: 'On a to-do', keys: [
    [['↑', '↓'], 'Move between to-dos (J and K too)'], [['Space'], 'Complete or reopen'],
    [['Enter'], 'Complete (as before)'], [['E'], 'Open it to edit'], [['F'], 'Flag'], [['D'], 'Set the date'],
    [['1', '2', '3', '4'], 'Priority'], [['Del'], 'Delete (with Undo)']] }
];

export function ShortcutsSheet({ Dialog, onClose, mac }) {
  return (
    <Dialog label="Keyboard shortcuts" onClose={onClose} className="tdk-keys">
      <div className="tdk-keys-body">
        {SHORTCUTS.map((g) => (
          <section key={g.title} className="tdk-keys-group">
            <h4>{g.title}</h4>
            <dl>
              {g.keys.map(([keys, what]) => (
                <div key={what} className="tdk-keys-row">
                  <dt>{keys.map((k, i) => (
                    <React.Fragment key={k}>
                      {i > 0 && <span className="tdk-then">{keys[0] === 'G' || keys[0] === 'V' ? 'then' : ''}</span>}
                      <kbd>{k === 'Ctrl' && mac ? '⌘' : k}</kbd>
                    </React.Fragment>
                  ))}</dt>
                  <dd>{what}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
